import { useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import ReactMarkdown from 'react-markdown';
import { CURRENCY_USD } from 'constants/currency';
import { MY_COACH_SETTINGS_PAGE, MY_SCHEDULE_PAGE, MY_SETTINGS_PAGE } from 'constants/pages';
import { EMPTY_COACH_AVATAR_SRC, getCoachFollowUrl } from 'constants/user';
import {
  CoachStatusEnum,
  FileFormatEnum,
  Reviews,
  useGetCoachReviewsByCoachIdLazyQuery,
} from 'types/generated/client';
import { trackEvent } from 'services/client/analytics';
import { ONBOARDING_LISTING_SHARED } from 'services/client/analytics/events';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getLessonItemizedTotal } from 'utils/shared/money/getLessonItemizedTotal';
import { getVideoAnalysisItemizedTotal } from 'utils/shared/money/getVideoAnalysisItemizedTotal';
import { pluralize } from 'utils/shared/pluralize';
import toTitleCase from 'utils/shared/string/toTitleCase';
import { calculateYearsOfCoachingExperience } from 'utils/shared/user/calculateYearsOfCoachingExperience';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useMobileView } from 'hooks/useMobileView';
import { useModal } from 'hooks/useModal';
import AddPhoto from 'svg/AddPhoto';
import Cog from 'svg/Cog';
import FaceBookIcon from 'svg/FaceBookIcon';
import Fee from 'svg/Fee';
import Globe from 'svg/Globe';
import Group from 'svg/Group';
import InstagramIcon from 'svg/InstagramIcon';
import LinkedInIcon from 'svg/LinkedInIcon';
import Location from 'svg/Location';
import PaddleIcon from 'svg/PaddleIcon';
import Pencil from 'svg/Pencil';
import Share from 'svg/Share';
import Star from 'svg/Star';
import XIcon from 'svg/XIcon';
import YoutubeIcon from 'svg/YoutubeIcon';
import Badge from 'components/Badge';
import { Button, ButtonLink } from 'components/Button';
import useIsFollowingCoach from 'components/Coach/useIsFollowingCoach';
import Link from 'components/Link';
import MediaGallery from 'components/MediaGallery';
import Pill from 'components/Pill';
import { sportStyles } from 'components/VideoAnalysis/utils';
import ModalReviews from 'components/modals/ModalReviews';
import ModalShare from 'components/modals/ModalShare';
import classNames from 'styles/utils/classNames';
import CoachMap from './CoachMap';
import AddInfoBtn from './components/AddInfoBtn';
import ClinicDisplay from './components/ClinicDisplay';
import CoachStatCard from './components/CoachStatCard';
import DetailCard from './components/DetailCard';
import EditButton from './components/EditButton';
import PremiumSection from './components/PremiumSection';
import RemoteCoachingCard from './components/RemoteCoachingCard';
import ReviewSection from './components/ReviewSection';
import AboutMeFormModal from './formModals/AboutMeFormModal';
import ExperienceFormModal from './formModals/ExperienceFormModal';
import LessonsLikeFormModal from './formModals/LessonsLikeFormModal';
import PhotoFormModal from './formModals/PhotoFormModal';
import PrepareFormModal from './formModals/PrepareFormModal';
import TagsAndLinksFormModal from './formModals/TagsAndLinksFormModal';
import WhoITeachFormModal from './formModals/WhoITeachFormModal';
import { ContentProps, FormModalModeEnum, FormModalNamesEnum } from './types';
import Contacts from '../TournamentDetails/Contacts';

const IS_CONTACT_ENABLED = false;

export default function CoachContent({
  coach,
  coachStarterProfile,
  venues,
  handleBookVenue,
  isFollowingCoach,
  handleCoachFollowingBtnClick,
  hideToggleFollowBtn,
  handleGetStarted,
  onSubscribe,
  onEditPremiumPrice,
  onUploadContent,
  premiumCoachContent,
  isPlayerSubscribedToPremium,
  premiumContentLoading,
  isCoachOfPage,
  getCoachBookingData,
  viewAsClient,
  shareUrl,
  coachUpcomingClinicLessonsData,
  coachUpcomingClinicLessonsLoading,
  handleGetUpcomingLessonsById,
}: ContentProps) {
  const { position, getEstimatedLocation, centerLatitude, centerLongitude } = useGeoLocation();

  const router = useRouter();

  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const {
    openModal: openShareModal,
    closeModal: closeShareModal,
    isOpen: isShareModalOpen,
  } = useModal();

  const {
    openModal: openReviewsModal,
    closeModal: closeReviewsModal,
    isOpen: isReviewsModalOpen,
  } = useModal();

  const [formModalStates, setFormModalStates] = useState({
    [FormModalNamesEnum.photoFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
    [FormModalNamesEnum.prepareFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
    [FormModalNamesEnum.lessonLikeFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
    [FormModalNamesEnum.experienceFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
    [FormModalNamesEnum.aboutMeFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
    [FormModalNamesEnum.tagsAndLinksFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
    [FormModalNamesEnum.whoITeachFormModal]: { isOpen: false, mode: FormModalModeEnum.Add },
  });

  const { user } = useGetCurrentUser();
  const isCoach = user?.coachStatus === CoachStatusEnum.Active;
  const userId = user?.id;
  const coachId = coach?.id;
  const followersCount = coach?.followersAggregate?.aggregate?.count || 0;
  const isLiveCoach = !!coach?.id;
  const canCoachEditInfo = isCoachOfPage === true && viewAsClient === false;

  // Reviews API call
  const [getCoachReviewsByCoachId, { loading: reviewsLoading, data: reviewsData }] =
    useGetCoachReviewsByCoachIdLazyQuery();
  const reviews = reviewsData?.reviews || [];

  const isMobile = useMobileView();

  const openFormModal = (modalName: FormModalNamesEnum, mode = FormModalModeEnum.Add) => {
    setFormModalStates((prevState) => ({
      ...prevState,
      [modalName]: { isOpen: true, mode },
    }));
  };

  const closeFormModal = (modalName: FormModalNamesEnum, recallCoachApi: boolean) => {
    setFormModalStates((prevState) => ({
      ...prevState,
      [modalName]: { isOpen: false, mode: FormModalModeEnum.Add },
    }));
    if (recallCoachApi) {
      getCoachBookingData();
    }
  };

  const shareAction = () => {
    openShareModal();

    // Track the share attempt
    trackEvent(ONBOARDING_LISTING_SHARED, {
      platform: typeof navigator.share === 'function' ? 'native_share' : 'copy_link',
    });

    if (typeof navigator.share === 'function') {
      navigator
        .share({
          title: `${coach?.fullName || coachStarterProfile?.name} - book lessons on Bounce`,
          text: `More details at ${shareUrl}`,
          url: shareUrl,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing:', error));
    }
  };

  useEffect(() => {
    const getLocation = async () => {
      // if (!canUseExactLocation) {
      /**
       * @note exact was super slow, so turning on estimate for all cases until we can optimize
       */
      getEstimatedLocation()
        .then(() => {
          setIsPageLoaded(true);
        })
        .catch(() => {});
      // }
    };

    if (!position) {
      getLocation();
    } else {
      setIsPageLoaded(true);
    }
  }, [position]);

  // Fetch reviews when coachId is available
  useEffect(() => {
    if (coachId) {
      getCoachReviewsByCoachId({
        variables: {
          coachUserId: coachId,
        },
      }).catch((err) => {
        captureException(err);
      });
    }
  }, [coachId, getCoachReviewsByCoachId]);

  const distance =
    venues.length > 0
      ? Math.round(
          calculateHaversineDistance({
            coord1: {
              latitude: venues[0].latitude || 0,
              longitude: venues[0].longitude || 0,
            },
            coord2: {
              latitude: centerLatitude || 0,
              longitude: centerLongitude || 0,
            },
            unit: 'miles',
          }),
        )
      : 0;

  const onCourtCoachPrice = coach?.priceUnitAmountCoachDefault || 0;

  const remoteCoachPrice = coach?.priceUnitAmountRemoteCoachDefault || 0;

  const onCourtPriceParams = {
    priceUnitAmount: onCourtCoachPrice,
  };

  const remotePriceParams = {
    priceUnitAmount: remoteCoachPrice,
  };

  const prices =
    onCourtCoachPrice > 0
      ? getLessonItemizedTotal(onCourtPriceParams)
      : getVideoAnalysisItemizedTotal(remotePriceParams);

  const profileImageUrl =
    coach?.profileImageProviderUrl ||
    coachStarterProfile?.profileImageUrl ||
    coachStarterProfile?.aboutPhotoImageUrl ||
    coachStarterProfile?.logoImageUrl ||
    EMPTY_COACH_AVATAR_SRC;

  const coachAdditionalMedia =
    coach?.userFiles.map((userFile) => ({ url: userFile.url, format: userFile.fileFormat })) || [];
  const firstVideoIndex = coachAdditionalMedia.findIndex(
    (file) => file.format === FileFormatEnum.Video,
  );

  const galleryFiles = [
    { url: profileImageUrl, format: FileFormatEnum.Image },
    ...(coachAdditionalMedia[firstVideoIndex] ? [coachAdditionalMedia[firstVideoIndex]] : []),
    ...coachAdditionalMedia.filter((_file, index) => index !== firstVideoIndex),
  ];

  const socialIconClass = 'h-5 w-5 text-color-bg-lightmode-icon dark:text-color-bg-darkmode-icon';

  const socialLinks = [
    { icon: <Globe className={socialIconClass} />, url: coach?.websiteUrl },
    {
      icon: <InstagramIcon className={socialIconClass} />,
      url: coach?.instagramUrl,
    },
    {
      icon: <FaceBookIcon className={socialIconClass} />,
      url: coach?.facebookUrl,
    },
    {
      icon: <LinkedInIcon className={socialIconClass} />,
      url: coach?.linkedinUrl,
    },
    {
      icon: <YoutubeIcon className={socialIconClass} />,
      url: coach?.youtubeUrl,
    },
    { icon: <XIcon className={socialIconClass} />, url: coach?.xUrl },
  ].filter((socialLink) => typeof socialLink.url === 'string' && socialLink.url.trim() !== '');

  const sport = sportStyles[coach?.defaultSport as keyof typeof sportStyles];

  // SEO heading + intro for the coach. Rendered off-screen so the booking UI stays
  // untouched while remaining crawlable for search/AI.
  const coachName = coach?.fullName || coachStarterProfile?.name || '';
  const coachSportLabel = sport?.label || 'Pickleball';
  const coachCity =
    coach?.activeCity?.name || coach?.cityName || coachStarterProfile?.cityName || '';
  const coachState =
    coach?.activeCity?.countrySubdivision?.name ||
    coach?.countrySubdivision?.name ||
    coachStarterProfile?.stateName ||
    '';
  const coachLocation = [coachCity, coachState].filter(Boolean).join(', ');
  const coachSeoHeading = `${coachName} ${coachSportLabel} Coach${
    coachLocation ? ` in ${coachLocation}` : ''
  }`;
  const coachSeoDescription = `Train with ${coachName}, a ${coachSportLabel.toLowerCase()} coach${
    coachLocation ? ` in ${coachLocation}` : ''
  }. Browse lesson availability, training opportunities, and ways to improve your game.`;

  const sportTagJsx = sport ? (
    <div
      className={`flex h-5 items-center rounded-2xl ${sport.bgColor} px-2 text-xs ${sport.textColor} lg:h-6 lg:px-3 lg:text-sm`}
    >
      {sport.label}
    </div>
  ) : null;

  // Computed booleans for section visibility
  const hasAboutMe = !!(coach?.aboutMe || coachStarterProfile?.aboutMe);
  const shouldShowAboutMe = hasAboutMe || canCoachEditInfo;

  const hasExperience = !!(
    coach?.isPciMember ||
    // coach?.isPprMember ||
    coach?.coachingStartYear ||
    coach?.pickleballRatingScales?.length > 0
  );
  const shouldShowExperience = hasExperience || canCoachEditInfo;

  const hasWhoITeach = !!(
    coach?.coachLessonAgeGroups.length > 0 || coach?.coachLessonSkillLevels.length > 0
  );
  const shouldShowWhoITeach = hasWhoITeach || canCoachEditInfo;

  const hasLessonDescription = !!coach?.coachLessonDescription;
  const shouldShowLessonDescription = hasLessonDescription || canCoachEditInfo;

  const hasHowToPrepare = !!coach?.coachLessonHowToPrepare;
  const shouldShowHowToPrepare = hasHowToPrepare || canCoachEditInfo;

  // Additional section visibility
  const hasUpcomingLessons = !!(
    coachUpcomingClinicLessonsData?.lessonsAggregate?.aggregate?.count &&
    coachUpcomingClinicLessonsData.lessonsAggregate.aggregate.count > 0
  );
  const shouldShowUpcomingLessons = hasUpcomingLessons || canCoachEditInfo;

  const hasPremiumContent = (premiumCoachContent?.userContentFiles?.length ?? 0) > 0;
  const shouldShowPremiumContent = hasPremiumContent || canCoachEditInfo;

  const hasVenues = !!(venues?.length > 0);
  const shouldShowWhereITeach = hasVenues || canCoachEditInfo;

  // Reviews section - handles its own visibility internally based on data and user role

  // Helper to determine if we need a divider before each section
  const hasAnySectionAbove =
    shouldShowUpcomingLessons ||
    !!coach?.priceUnitAmountRemoteCoachDefault ||
    shouldShowPremiumContent;

  const hasAnySectionAboveExperience = hasAnySectionAbove || shouldShowAboutMe;
  const hasAnySectionAboveWhoITeach = hasAnySectionAboveExperience || shouldShowExperience;
  // Note: Reviews section manages its own visibility, so we assume it could be there for divider logic
  const hasAnySectionAboveWhereITeach =
    hasAnySectionAboveWhoITeach || shouldShowWhoITeach || !!coachId;
  const hasAnySectionAboveLessonsLike = hasAnySectionAboveWhereITeach || shouldShowWhereITeach;
  const hasAnySectionAboveHowToPrepare =
    hasAnySectionAboveLessonsLike || shouldShowLessonDescription;

  return (
    <>
      <div>
        <div className="relative">
          <MediaGallery
            maxPreviews={canCoachEditInfo ? 1 : isMobile ? 1 : 2}
            files={galleryFiles}
            showViewMoreButton={!canCoachEditInfo}
            addMorePlaceholder={
              canCoachEditInfo ? (
                <AddInfoBtn
                  label="Add More Photos"
                  className="!m-0 h-full shrink-0 basis-[50%] max-sm:hidden"
                  onClick={() => openFormModal(FormModalNamesEnum.photoFormModal)}
                />
              ) : null
            }
          />
          {canCoachEditInfo && (
            <EditButton
              icon={<AddPhoto className="h-4 w-4" />}
              onClick={() =>
                openFormModal(FormModalNamesEnum.photoFormModal, FormModalModeEnum.Edit)
              }
              className={classNames('absolute bottom-6  right-4 mt-0')}
            />
          )}
        </div>
        <div className="px-4 lg:p-0">
          {!!coachName && (
            <div className="fixed -left-40 -top-40 -z-100">
              <h1>{coachSeoHeading}</h1>
              <p>{coachSeoDescription}</p>
            </div>
          )}
          <div className="mt-4 flex  justify-between lg:mt-ds-xl">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="typography-product-heading capitalize text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {coach?.fullName || coachStarterProfile?.name || ''}
                </h2>

                <div className="flex items-center gap-2">
                  {sportTagJsx}
                  {/* {coach.isQuatroPickleballMember && (
                    <img src="/images/quatro/quatro-ribbon.png" alt="quatro-ribbon" />
                  )} */}
                </div>
              </div>
              <div className=" mt-ds-md flex items-center  gap-4">
                {socialLinks.map((socialLink) => (
                  <Link href={socialLink.url!}>{socialLink.icon}</Link>
                ))}
                {canCoachEditInfo && socialLinks.length !== 0 && (
                  <EditButton
                    onClick={() => openFormModal(FormModalNamesEnum.tagsAndLinksFormModal)}
                  />
                )}
                {socialLinks.length === 0 && canCoachEditInfo && (
                  <AddInfoBtn
                    onClick={() =>
                      openFormModal(
                        FormModalNamesEnum.tagsAndLinksFormModal,
                        FormModalModeEnum.Edit,
                      )
                    }
                    label="Add social media"
                    className="!w-fit !py-1"
                  />
                )}
              </div>
              <div
                className={classNames(
                  'mt-5 flex items-center gap-ds-xl',
                  canCoachEditInfo && 'mt-ds-md',
                )}
              >
                {
                  <div className="flex gap-1">
                    {coach?.activeCity ? (
                      <>
                        <Location className="mr-1 h-5 w-5" />
                        <span className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                          {coach?.activeCity?.name}, {coach?.activeCity?.countrySubdivision?.name}
                        </span>
                      </>
                    ) : (
                      isCoachOfPage && (
                        <span className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                          No city set
                        </span>
                      )
                    )}
                  </div>
                }

                {canCoachEditInfo && <EditButton onClick={() => router.push(MY_SETTINGS_PAGE)} />}
              </div>
              {/* {!!isLiveCoach && (
                <p>
                  <span className="typography-product-subheading mr-ds-sm">{followersCount}</span>
                  <span className="typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {followersCount === 1 ? 'Follower' : 'Followers'}
                  </span>
                </p>
              )} */}
            </div>
            <div className="flex basis-[15rem] justify-end gap-4">
              <div>
                {!isCoachOfPage && !hideToggleFollowBtn && (
                  <Button
                    variant={isFollowingCoach ? 'secondary' : 'primary'}
                    size="md"
                    isInline
                    onClick={() => handleCoachFollowingBtnClick(!isFollowingCoach)}
                    className="min-h-10 min-w-40"
                  >
                    {isFollowingCoach ? 'Following' : 'Follow'}
                  </Button>
                )}
              </div>

              {/* <Button
                onClick={shareAction}
                iconLeft={<Share className="inline-block h-5 text-color-text-darkmode-primary" />}
                variant="primary"
                sizeMobile="sm"
                sizeDesktop="md"
                isInline
              >
                Share
              </Button> */}
            </div>
          </div>

          <div className="mt-ds-3xl grid  grid-cols-3 gap-ds-lg max-md:grid-cols-2 max-sm:grid-cols-1">
            {/* <DetailCard
              Icon={Location}
              title="Location"
              description={`${pluralize({
                count: distance,
                singular: 'mile',
                plural: 'miles',
              })} away`}
            />
            <DetailCard
              Icon={Fee}
              title={`${onCourtCoachPrice > 0 ? 'Price on-court' : 'Remote Price'}`}
              description={`${
                prices?.priceUnitAmount === 0
                  ? convertUnitPriceToFormattedPrice(
                      prices?.customerApplicationFee || 0,
                      coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD,
                    ).priceDisplay
                  : onCourtCoachPrice > 0
                  ? convertUnitPriceToFormattedPrice(
                      prices?.orderSubtotalWithPlatformFeesUnitAmount || 0,
                      coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD,
                    ).priceDisplay
                  : convertUnitPriceToFormattedPrice(
                      prices?.orderSubtotal || 0,
                      coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD,
                    ).priceDisplay
              }`}
            /> */}
            <CoachStatCard
              icon={
                <Star className="h-6 w-6 text-color-text-lightmode-inactive dark:text-color-text-darkmode-inactive" />
              }
              value={reviews.length}
              label={pluralize({
                count: reviews.length,
                singular: 'review',
                plural: 'reviews',
                hideCount: true,
              })}
            />
            <CoachStatCard
              icon={
                <PaddleIcon className="h-6 w-6 text-color-text-lightmode-inactive dark:text-color-text-darkmode-inactive" />
              }
              value={coach?.coachLessonsAggregate.aggregate?.count || 0}
              label={pluralize({
                count: coach?.coachLessonsAggregate.aggregate?.count || 0,
                singular: 'lesson',
                plural: 'lessons',
                hideCount: true,
              })}
            />
            <CoachStatCard
              icon={
                <Group className="h-6 w-6 text-color-text-lightmode-inactive dark:text-color-text-darkmode-inactive" />
              }
              value={followersCount}
              label={pluralize({
                count: followersCount,
                singular: 'follower',
                plural: 'followers',
                hideCount: true,
              })}
            />
          </div>
          {/* Mobile View */}
          <div className="mt-10 flex-col items-center justify-center gap-2 xs:flex lg:hidden">
            {isCoach && userId === coachId && (
              <>
                <Button
                  onClick={shareAction}
                  iconLeft={<Share className="inline-block h-5 text-color-text-darkmode-primary" />}
                  variant="primary"
                  size="md"
                  sizeMobile="lg"
                  isInline
                  className="xs:w-full"
                >
                  Share my listing
                </Button>
                {/* <ButtonLink
                  href={MY_COACH_SETTINGS_PAGE}
                  iconLeft={
                    <Pencil className="inline-block h-5 text-color-text-lightmode-primary" />
                  }
                  variant="secondary"
                  size="md"
                  sizeMobile="lg"
                  isInline
                  className="xs:w-full"
                >
                  Edit Listing
                </ButtonLink> */}

                <ButtonLink
                  href={MY_SETTINGS_PAGE}
                  iconLeft={<Cog className="inline-block h-5 text-color-text-lightmode-primary" />}
                  variant="secondary"
                  size="md"
                  sizeMobile="lg"
                  isInline
                  className="xs:w-full"
                >
                  Settings
                </ButtonLink>
              </>
            )}
          </div>

          {/* First divider only shows if any main content section is visible */}
          {(shouldShowUpcomingLessons ||
            !!coach?.priceUnitAmountRemoteCoachDefault ||
            shouldShowPremiumContent) && <hr className="my-ds-3xl" />}

          {shouldShowUpcomingLessons && (
            <ClinicDisplay
              coachUpcomingClinicLessonsData={coachUpcomingClinicLessonsData}
              loading={coachUpcomingClinicLessonsLoading}
              handleGetUpcomingLessonsById={handleGetUpcomingLessonsById}
            />
          )}

          {/* Remote Coaching */}
          {!!coach?.priceUnitAmountRemoteCoachDefault && (
            <div className="mt-ds-2xl px-2 lg:px-0">
              <RemoteCoachingCard
                responseTime="<2 day"
                reviewCount={15}
                price={
                  convertUnitPriceToFormattedPrice(
                    coach?.priceUnitAmountRemoteCoachDefault,
                    coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD,
                  ).priceFormatted
                }
                currency={
                  coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD
                }
                // rating={1}
                handleGetStarted={handleGetStarted}
              />
            </div>
          )}

          {shouldShowPremiumContent && (
            <PremiumSection
              onEditPremiumPrice={onEditPremiumPrice}
              onUploadContent={onUploadContent}
              onSubscribe={onSubscribe}
              isOwner={isCoachOfPage}
              coachId={coachId}
              premiumCoachContent={premiumCoachContent}
              isPlayerSubscribedToPremium={isPlayerSubscribedToPremium}
              premiumContentLoading={premiumContentLoading}
              canCoachEditInfo={canCoachEditInfo}
            />
          )}

          {shouldShowAboutMe && (
            <div
              className={`my-ds-2xl px-2 lg:px-0 ${
                hasAnySectionAbove
                  ? 'border-t border-color-border-input-lightmode pt-ds-2xl dark:border-color-border-input-darkmode lg:pt-ds-3xl'
                  : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  About Me
                </h2>
                {canCoachEditInfo && (
                  <EditButton
                    onClick={() =>
                      openFormModal(FormModalNamesEnum.aboutMeFormModal, FormModalModeEnum.Edit)
                    }
                    className="ml-auto"
                  />
                )}
              </div>
              {hasAboutMe ? (
                <div className="prose mt-ds-sm dark:prose-invert">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <p
                          className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                          {...props}
                        />
                      ),
                      li: ({ node, ...props }) => (
                        <li
                          className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                          {...props}
                        />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          className="text-color-text-lightmode-secondary underline dark:text-color-text-darkmode-secondary"
                          {...props}
                          target="_blank"
                          rel="noreferrer noopener nofollow"
                        />
                      ),
                    }}
                  >
                    {coach?.aboutMe || coachStarterProfile?.aboutMe || ''}
                  </ReactMarkdown>
                </div>
              ) : (
                canCoachEditInfo && (
                  <AddInfoBtn
                    label="Add about me information"
                    onClick={() => openFormModal(FormModalNamesEnum.aboutMeFormModal)}
                  />
                )
              )}
            </div>
          )}
          {shouldShowExperience && (
            <div
              className={`mt-ds-2xl px-2 lg:px-0 ${
                hasAnySectionAboveExperience
                  ? 'border-t border-color-border-input-lightmode pt-ds-2xl dark:border-color-border-input-darkmode lg:pt-ds-3xl'
                  : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  My experience
                </h2>
                {canCoachEditInfo && (
                  <EditButton
                    onClick={() =>
                      openFormModal(FormModalNamesEnum.experienceFormModal, FormModalModeEnum.Edit)
                    }
                    className="ml-auto"
                  />
                )}
              </div>
              {hasExperience ? (
                <div className="flex flex-wrap gap-x-3 gap-y-5 pt-2">
                  {/* {coach?.isQuatroPickleballMember && <Badge label={`Quatro Certified`} />} */}
                  {/* {coach?.isIptpaMember && <Badge label={`IPTPA Certified`} />} */}
                  {/* {coach?.isPciMember && <Badge label={`PCI Certified`} />} */}
                  {/* {coach?.isPprMember && <Badge label={`PPR Certified`} />} */}
                  {coach?.coachingStartYear && (
                    <Badge
                      label={pluralize({
                        count: calculateYearsOfCoachingExperience(coach?.coachingStartYear),
                        singular: 'Year Coaching',
                        plural: 'Years Coaching',
                      })}
                    />
                  )}
                  {coach?.pickleballRatingScales?.map((rate, i) => (
                    <Badge key={i} label={`${rate.scale.shortName}: ${rate.rating}`} />
                  ))}
                </div>
              ) : (
                canCoachEditInfo && (
                  <AddInfoBtn
                    onClick={() => openFormModal(FormModalNamesEnum.experienceFormModal)}
                    label="Add experience"
                  />
                )
              )}
            </div>
          )}
          {shouldShowWhoITeach && (
            <div
              className={`mt-ds-2xl px-2 lg:px-0 ${
                hasAnySectionAboveWhoITeach
                  ? 'border-t border-color-border-input-lightmode pt-ds-2xl dark:border-color-border-input-darkmode lg:pt-ds-3xl'
                  : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Who I teach
                </h2>
                {canCoachEditInfo && (
                  <EditButton
                    onClick={() =>
                      openFormModal(FormModalNamesEnum.whoITeachFormModal, FormModalModeEnum.Edit)
                    }
                    className="ml-auto"
                  />
                )}
              </div>

              {hasWhoITeach ? (
                <div className="flex flex-wrap gap-x-3 gap-y-5 pt-2">
                  {coach?.coachLessonAgeGroups?.map((age, i) => (
                    <Badge key={i} label={`${toTitleCase(age.lessonAgeGroup)}`} />
                  ))}
                  {coach?.coachLessonSkillLevels?.map((coachSkill, i) => (
                    <Badge key={i} label={`${toTitleCase(coachSkill.skillLevel)}`} />
                  ))}
                </div>
              ) : (
                canCoachEditInfo && (
                  <AddInfoBtn
                    label="Add information"
                    onClick={() => openFormModal(FormModalNamesEnum.whoITeachFormModal)}
                  />
                )
              )}
            </div>
          )}
          <ReviewSection
            coachId={coachId}
            canCoachEditInfo={canCoachEditInfo}
            reviews={reviews as Reviews[]}
            loading={reviewsLoading}
          />
          {shouldShowWhereITeach && (
            <div
              className={`mt-ds-2xl ${
                hasAnySectionAboveWhereITeach
                  ? 'border-t border-color-border-input-lightmode pt-ds-2xl dark:border-color-border-input-darkmode lg:pt-ds-3xl'
                  : ''
              }`}
            >
              <div className="mb-ds-lg flex items-center justify-between gap-2">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Where I teach &bull;{' '}
                  {pluralize({
                    count: venues?.length,
                    singular: 'location',
                    plural: 'locations',
                  })}
                </h2>
                {canCoachEditInfo && (
                  <EditButton
                    onClick={() => router.push(`${MY_SCHEDULE_PAGE}?edit=true`)}
                    className="ml-auto"
                  />
                )}
              </div>

              <CoachMap
                venues={venues}
                centerLatitude={centerLatitude}
                centerLongitude={centerLongitude}
                handleBookHere={handleBookVenue}
              />
            </div>
          )}
          {shouldShowLessonDescription && (
            <div
              className={`my-ds-2xl px-2 lg:px-0 ${
                hasAnySectionAboveLessonsLike
                  ? 'border-t border-color-border-input-lightmode pt-ds-2xl dark:border-color-border-input-darkmode lg:pt-ds-3xl'
                  : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  What my lessons are like
                </h2>
                {canCoachEditInfo && (
                  <EditButton
                    onClick={() =>
                      openFormModal(FormModalNamesEnum.lessonLikeFormModal, FormModalModeEnum.Edit)
                    }
                    className="ml-auto"
                  />
                )}
              </div>
              {hasLessonDescription ? (
                <p className="typography-product-body mt-ds-sm whitespace-pre-line break-words text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {coach?.coachLessonDescription}
                </p>
              ) : (
                canCoachEditInfo && (
                  <AddInfoBtn
                    onClick={() => openFormModal(FormModalNamesEnum.lessonLikeFormModal)}
                    label="Add information"
                  />
                )
              )}
            </div>
          )}
          {shouldShowHowToPrepare && (
            <div
              className={`my-ds-2xl px-2 lg:px-0 ${
                hasAnySectionAboveHowToPrepare
                  ? 'border-t border-color-border-input-lightmode pt-ds-2xl dark:border-color-border-input-darkmode lg:pt-ds-3xl'
                  : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  How to prepare
                </h2>
                {canCoachEditInfo && (
                  <EditButton
                    onClick={() =>
                      openFormModal(FormModalNamesEnum.prepareFormModal, FormModalModeEnum.Edit)
                    }
                    className="ml-auto"
                  />
                )}
              </div>
              {hasHowToPrepare ? (
                <p className="typography-product-body mt-ds-sm whitespace-pre-line break-words  text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {coach?.coachLessonHowToPrepare}
                </p>
              ) : (
                canCoachEditInfo && (
                  <AddInfoBtn
                    onClick={() => openFormModal(FormModalNamesEnum.prepareFormModal)}
                    label="Add information"
                  />
                )
              )}
            </div>
          )}
          <div className="mt-ds-2xl border-t border-color-border-input-lightmode px-2 dark:border-color-border-input-darkmode xs:pt-ds-2xl lg:px-0 lg:pt-ds-3xl">
            <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Cancellation Policy
            </h2>
            <div className="typography-product-body mt-ds-sm flex	flex-col justify-between gap-y-10 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary xs:mb-28 md:mb-24">
              <p>
                {`If you need to cancel or reschedule your lesson, no problem! You can get a full refund if you cancel within 24 hours of making your booking.`}
              </p>
              <p>
                {`If you would like to modify a lesson, you can easily make these changes within 24 hours of booking, and up to 72 hours before your lesson starts.`}
              </p>
              <p>
                {`Please let us know if you have any questions about the process! You can reach us at team@bounce.game`}
              </p>
            </div>
          </div>
          {IS_CONTACT_ENABLED && <Contacts />}
        </div>
      </div>
      <ModalShare
        isOpen={isShareModalOpen}
        closeModal={closeShareModal}
        shareUrl={shareUrl}
        title="Share this link"
      />
      <ModalReviews
        isOpen={isReviewsModalOpen}
        closeModal={closeReviewsModal}
        title="Share this link"
      />
      {/* forms to update in modal*/}
      <PhotoFormModal
        isOpen={formModalStates[FormModalNamesEnum.photoFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.photoFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.photoFormModal, recallCoachApi)
        }
        coachData={coach}
      />

      <TagsAndLinksFormModal
        isOpen={formModalStates[FormModalNamesEnum.tagsAndLinksFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.tagsAndLinksFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.tagsAndLinksFormModal, recallCoachApi)
        }
        coachData={coach}
      />
      <AboutMeFormModal
        isOpen={formModalStates[FormModalNamesEnum.aboutMeFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.aboutMeFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.aboutMeFormModal, recallCoachApi)
        }
        coachData={coach}
      />
      <ExperienceFormModal
        isOpen={formModalStates[FormModalNamesEnum.experienceFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.experienceFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.experienceFormModal, recallCoachApi)
        }
        coachData={coach}
      />
      <WhoITeachFormModal
        isOpen={formModalStates[FormModalNamesEnum.whoITeachFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.whoITeachFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.whoITeachFormModal, recallCoachApi)
        }
        coachData={coach}
      />
      <LessonsLikeFormModal
        isOpen={formModalStates[FormModalNamesEnum.lessonLikeFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.lessonLikeFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.lessonLikeFormModal, recallCoachApi)
        }
        coachData={coach}
      />
      <PrepareFormModal
        isOpen={formModalStates[FormModalNamesEnum.prepareFormModal].isOpen}
        mode={formModalStates[FormModalNamesEnum.prepareFormModal].mode}
        handleClose={(recallCoachApi = false) =>
          closeFormModal(FormModalNamesEnum.prepareFormModal, recallCoachApi)
        }
        coachData={coach}
      />
    </>
  );
}
